// Aba "Cobranças" da tela Financeiro.
// Lista os documentos financeiros, abre o detalhe (itens + parcelas), registra
// baixa manual e cria o "orçamento rápido" (cobrança sem OS).
// A criação a partir de uma OS fica na aba Prévia (Financeiro.tsx).
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { mascaraMoedaBR, moedaParaNumero, numeroParaMascaraMoeda, quantidadeParaNumero } from '../lib/moeda';
import { linkWhatsapp } from '../lib/whatsapp';

export interface Parcela {
  id: number;
  numero: number;
  valor: number | string;
  vencimento: string;
  status: string;
  pago_em: string | null;
  vencida?: boolean;
}

export interface ItemDocumento {
  id: number;
  tipo: string;
  descricao: string;
  quantidade: number | string;
  valor_unitario: number | string;
  valor_total: number | string;
  origem_tabela: string | null;
}

export interface Documento {
  id: number;
  codigo: string;
  origem: string;
  ordem_servico_id: number | null;
  os_codigo: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  cliente_whatsapp?: string | null;
  cliente_telefone?: string | null;
  descricao: string | null;
  status: string;
  data_emissao: string | null;
  subtotal: number | string;
  desconto: number | string;
  acrescimo: number | string;
  total: number | string;
  created_at: string;
  parcelas_qtd: number;
  parcelas_pagas: number;
  parcelas_vencidas: number;
  proximo_vencimento: string | null;
  valor_pago: number | string;
  aprovado_em?: string | null;
  aprovado_nome?: string | null;
  link_publico?: string | null;
  link_pdf?: string | null;
  itens?: ItemDocumento[];
  parcelas?: Parcela[];
}

interface ClienteResumo { id: number; nome: string; whatsapp?: string | null; telefone?: string | null }
interface MaterialResumo { id: number; nome: string; valor_unitario: number | string; unidade_medida: string | null }
interface TecnicoResumo { id: number; nome: string; valor_hora: number | string | null; status?: string }
// Tipo de chamado vem de Cadastros > Categorias — a MESMA fonte que a tela de criar OS
// usa (`/cadastros/categorias.php?somente_ativos=1`, lista completa, sem filtrar por
// `tipo`). Se filtrasse aqui, as duas telas mostrariam opções diferentes.
interface CategoriaResumo { id: number; nome: string }

interface ItemRapido { tipo: string; descricao: string; quantidade: string; valor_unitario: string }

const num = (v: unknown): number => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const fmtMoeda = (v: unknown): string => num(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtData(v: string | null): string {
  if (!v) return '-';
  const d = new Date(v.length <= 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
}

const CORES_STATUS: Record<string, { fundo: string; cor: string; rotulo: string }> = {
  rascunho: { fundo: '#f1f5f9', cor: '#475569', rotulo: 'Rascunho' },
  emitido: { fundo: '#eff6ff', cor: '#1d4ed8', rotulo: 'Emitido' },
  pago: { fundo: '#f0fdf4', cor: '#166534', rotulo: 'Pago' },
  cancelado: { fundo: '#fef2f2', cor: '#b91c1c', rotulo: 'Cancelado' },
};

const MEIOS = [
  { v: 'pix', t: 'Pix' },
  { v: 'boleto', t: 'Boleto' },
  { v: 'cartao', t: 'Cartão' },
  { v: 'transferencia', t: 'Transferência' },
  { v: 'dinheiro', t: 'Dinheiro' },
  { v: 'outro', t: 'Outro' },
];

const TIPOS_ITEM = [
  { v: 'servico', t: 'Serviço' },
  { v: 'material', t: 'Material' },
  { v: 'mao_obra', t: 'Mão de obra' },
  { v: 'deslocamento', t: 'Deslocamento' },
  { v: 'taxa', t: 'Taxa' },
];

const itemVazio = (): ItemRapido => ({ tipo: 'servico', descricao: '', quantidade: '1', valor_unitario: '' });

// Mensagem que vai no WhatsApp do cliente: CURTA e com o link do recibo.
// O detalhamento (itens, parcelas, logo) fica no recibo do link — mandar a lista
// inteira no WhatsApp fica poluído e quebra a formatação no celular.
function mensagemWhatsapp(d: Documento): string {
  const ehOrcamento = d.origem === 'rapido';
  const rotulo = ehOrcamento ? 'orçamento' : 'cobrança';
  const parcelas = d.parcelas || [];
  const linhas: string[] = [];

  linhas.push(`Olá${d.cliente_nome ? ' ' + d.cliente_nome : ''}, tudo bem?`);
  linhas.push('');
  linhas.push(`Segue o ${rotulo} *${d.codigo}*${d.descricao ? ` — ${d.descricao}` : ''}.`);
  if (d.os_codigo) linhas.push(`Ordem de serviço: ${d.os_codigo}`);
  linhas.push('');
  linhas.push(`*Total: R$ ${fmtMoeda(d.total)}*`);
  if (parcelas.length === 1) {
    linhas.push(`Vencimento: ${fmtData(parcelas[0].vencimento)}`);
  } else if (parcelas.length > 1) {
    linhas.push(`Em ${parcelas.length}x, primeiro vencimento em ${fmtData(parcelas[0].vencimento)}`);
  }
  linhas.push('');
  if (d.link_publico) {
    linhas.push(ehOrcamento && !d.aprovado_em
      ? 'Veja o recibo completo, baixe em PDF e aprove por aqui:'
      : 'Veja o recibo completo e baixe em PDF por aqui:');
    linhas.push(d.link_publico);
    linhas.push('');
  }
  linhas.push('Qualquer dúvida, estamos à disposição.');
  return linhas.join('\n');
}

export default function FinanceiroCobrancas({ onMensagem }: { onMensagem: (m: string) => void }) {
  const [lista, setLista] = useState<Documento[]>([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState<number | null>(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [detalhe, setDetalhe] = useState<Documento | null>(null);
  const [abertoId, setAbertoId] = useState<number | null>(null);

  // formulário do orçamento rápido
  const [rapidoAberto, setRapidoAberto] = useState(false);
  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [materiais, setMateriais] = useState<MaterialResumo[]>([]);
  const [tecnicos, setTecnicos] = useState<TecnicoResumo[]>([]);
  // Gerar OS a partir de orçamento pago
  const [gerarOsDoc, setGerarOsDoc] = useState<Documento | null>(null);
  const [gTipoOs, setGTipoOs] = useState('');
  const [gPrioridade, setGPrioridade] = useState('media');
  const [tiposChamado, setTiposChamado] = useState<CategoriaResumo[]>([]);
  const [rClienteId, setRClienteId] = useState('');
  const [rDescricao, setRDescricao] = useState('');
  const [rVencimento, setRVencimento] = useState('');
  const [rParcelas, setRParcelas] = useState('1');
  const [rDesconto, setRDesconto] = useState('');
  const [rAcrescimo, setRAcrescimo] = useState('');
  const [rItens, setRItens] = useState<ItemRapido[]>([itemVazio()]);

  const carregar = useCallback(async () => {
    setErro('');
    setCarregando(true);
    try {
      const q = new URLSearchParams({ limite: '100' });
      if (filtroStatus) q.set('status', filtroStatus);
      const r = await api<{ linhas: Documento[] }>(`/financeiro/financeiro_documentos.php?${q.toString()}`);
      setLista(r.linhas || []);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setCarregando(false);
    }
  }, [filtroStatus]);

  useEffect(() => { carregar(); }, [carregar]);

  async function abrirDetalhe(id: number) {
    if (abertoId === id) { setAbertoId(null); setDetalhe(null); return; }
    setErro('');
    setAbertoId(id);
    setDetalhe(null);
    try {
      setDetalhe(await api<Documento>(`/financeiro/financeiro_documentos.php?id=${id}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  async function mudarStatus(doc: Documento, status: string) {
    setErro('');
    setOcupado(doc.id);
    try {
      await api('/financeiro/financeiro_documentos.php', { method: 'PUT', body: { id: doc.id, status } });
      onMensagem(status === 'emitido' ? `Cobrança ${doc.codigo} emitida.` : `Cobrança ${doc.codigo} cancelada.`);
      await carregar();
      if (abertoId === doc.id) setDetalhe(await api<Documento>(`/financeiro/financeiro_documentos.php?id=${doc.id}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(null);
    }
  }

  async function excluirRascunho(doc: Documento) {
    setErro('');
    setOcupado(doc.id);
    try {
      await api(`/financeiro/financeiro_documentos.php?id=${doc.id}`, { method: 'DELETE' });
      onMensagem(`Rascunho ${doc.codigo} excluído.`);
      setAbertoId(null);
      setDetalhe(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(null);
    }
  }

  async function darBaixa(parcela: Parcela, meio: string) {
    setErro('');
    setOcupado(parcela.id);
    try {
      const r = await api<{ documento_pago: boolean }>('/financeiro/financeiro_recebimento.php', {
        method: 'POST',
        body: { parcela_id: parcela.id, meio, recebido_em: new Date().toISOString().slice(0, 10) },
      });
      onMensagem(r.documento_pago ? 'Recebimento registrado. Cobrança quitada.' : 'Recebimento registrado.');
      await carregar();
      if (abertoId) setDetalhe(await api<Documento>(`/financeiro/financeiro_documentos.php?id=${abertoId}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(null);
    }
  }

  // Carrega os cadastros que alimentam os selects do formulário (clientes, materiais
  // e técnicos com valor/hora). Falha em um não impede os outros.
  async function abrirRapido() {
    setRapidoAberto((a) => !a);
    if (clientes.length || materiais.length || tecnicos.length) return;
    const [c, m, t] = await Promise.allSettled([
      api<ClienteResumo[]>('/cadastros/clientes.php'),
      api<MaterialResumo[]>('/estoque/materiais.php'),
      api<TecnicoResumo[]>('/usuarios/usuarios.php?perfil=tecnico'),
    ]);
    if (c.status === 'fulfilled') setClientes(c.value || []);
    if (m.status === 'fulfilled') setMateriais(m.value || []);
    if (t.status === 'fulfilled') setTecnicos((t.value || []).filter((x) => x.status !== 'inativo'));
    const falhou = [c, m, t].find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
    if (falhou) setErro(falhou.reason instanceof Error ? falhou.reason.message : String(falhou.reason));
  }

  // Preenche descrição e valor unitário a partir do cadastro escolhido.
  function aplicarMaterial(i: number, materialId: string) {
    const m = materiais.find((x) => String(x.id) === materialId);
    if (!m) return;
    setRItens((a) => a.map((x, j) => j === i ? {
      ...x,
      descricao: m.nome,
      valor_unitario: numeroParaMascaraMoeda(m.valor_unitario),
    } : x));
  }

  function aplicarTecnico(i: number, tecnicoId: string) {
    const t = tecnicos.find((x) => String(x.id) === tecnicoId);
    if (!t) return;
    setRItens((a) => a.map((x, j) => j === i ? {
      ...x,
      descricao: `Mão de obra - ${t.nome}`,
      valor_unitario: numeroParaMascaraMoeda(t.valor_hora),
    } : x));
  }

  // Abre o painel de Gerar OS carregando os tipos de chamado de Cadastros > Categorias.
  async function abrirGerarOs(d: Documento) {
    setErro('');
    setGerarOsDoc(d);
    if (tiposChamado.length) return;
    try {
      const cats = await api<CategoriaResumo[]>('/cadastros/categorias.php?somente_ativos=1');
      setTiposChamado(cats || []);
      // mesmo comportamento da tela de criar OS: já vem com o primeiro selecionado
      setGTipoOs((atual) => atual || cats?.[0]?.nome || '');
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }

  async function gerarOs() {
    if (!gerarOsDoc) return;
    setErro('');
    if (!gTipoOs.trim()) { setErro('Informe o tipo de chamado da OS.'); return; }
    setOcupado(-2);
    try {
      const r = await api<{ codigo: string }>('/financeiro/financeiro_gerar_os.php', {
        method: 'POST',
        body: { documento_id: gerarOsDoc.id, tipo_os: gTipoOs.trim(), prioridade: gPrioridade },
      });
      onMensagem(`OS ${r.codigo} criada a partir do orçamento ${gerarOsDoc.codigo}.`);
      setGerarOsDoc(null);
      setGTipoOs('');
      setGPrioridade('media');
      await carregar();
      if (abertoId) setDetalhe(await api<Documento>(`/financeiro/financeiro_documentos.php?id=${abertoId}`));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(null);
    }
  }

  const totalRapido = rItens.reduce(
    (acc, it) => acc + quantidadeParaNumero(it.quantidade) * moedaParaNumero(it.valor_unitario), 0,
  ) - moedaParaNumero(rDesconto) + moedaParaNumero(rAcrescimo);

  async function salvarRapido() {
    setErro('');
    if (!rClienteId) { setErro('Selecione o cliente do orçamento rápido.'); return; }
    if (!rVencimento) { setErro('Informe a data de vencimento.'); return; }
    // Converte a mascara para numero antes de enviar: a API recebe 1234.56, nunca "1.234,56".
    const itens = rItens
      .filter((it) => it.descricao.trim() !== '')
      .map((it) => ({
        tipo: it.tipo,
        descricao: it.descricao.trim(),
        quantidade: quantidadeParaNumero(it.quantidade) || 1,
        valor_unitario: moedaParaNumero(it.valor_unitario),
      }));
    if (!itens.length) { setErro('Informe ao menos um item com descrição.'); return; }

    setOcupado(-1);
    try {
      await api('/financeiro/financeiro_documentos.php', {
        method: 'POST',
        body: {
          origem: 'rapido',
          cliente_id: Number(rClienteId),
          descricao: rDescricao.trim() || null,
          vencimento: rVencimento,
          parcelas_qtd: Number(rParcelas) || 1,
          desconto: moedaParaNumero(rDesconto),
          acrescimo: moedaParaNumero(rAcrescimo),
          itens,
        },
      });
      onMensagem('Orçamento rápido criado como rascunho.');
      setRapidoAberto(false);
      setRClienteId(''); setRDescricao(''); setRVencimento(''); setRParcelas('1');
      setRDesconto(''); setRAcrescimo(''); setRItens([itemVazio()]);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div>
      {erro && <div className="msg-erro">{erro}</div>}

      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', justifyContent: 'space-between' }}>
        <label style={{ minWidth: 200 }}>
          Status
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="rascunho">Rascunho</option>
            <option value="emitido">Emitido</option>
            <option value="pago">Pago</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sec sm" onClick={carregar} disabled={carregando}>
            {carregando ? 'Carregando...' : 'Atualizar'}
          </button>
          <button className="btn sm" onClick={abrirRapido}>
            {rapidoAberto ? 'Fechar orçamento rápido' : '+ Orçamento rápido'}
          </button>
        </div>
      </div>

      {rapidoAberto && (
        <div className="card" style={{ marginBottom: 16, display: 'grid', gap: 14 }}>
          <div>
            <strong style={{ color: '#0f172a' }}>Orçamento rápido</strong>
            <div style={{ marginTop: 4, color: 'var(--texto-suave)', fontSize: 13 }}>
              Cobrança sem OS — itens digitados na mão. Nasce como rascunho.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <label>
              Cliente
              <select value={rClienteId} onChange={(e) => setRClienteId(e.target.value)}>
                <option value="">Selecione...</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </label>
            <label>
              Descrição (opcional)
              <input className="input" value={rDescricao} onChange={(e) => setRDescricao(e.target.value)} maxLength={180} />
            </label>
            <label>
              1º vencimento
              <input className="input" type="date" value={rVencimento} onChange={(e) => setRVencimento(e.target.value)} />
            </label>
            <label>
              Parcelas
              <input className="input" inputMode="numeric" value={rParcelas}
                     onChange={(e) => setRParcelas(e.target.value.replace(/[^\d]/g, ''))} />
            </label>
            <label>
              Desconto (R$)
              <input className="input" inputMode="numeric" value={rDesconto} placeholder="0,00"
                     onChange={(e) => setRDesconto(mascaraMoedaBR(e.target.value))} />
            </label>
            <label>
              Acréscimo (R$)
              <input className="input" inputMode="numeric" value={rAcrescimo} placeholder="0,00"
                     onChange={(e) => setRAcrescimo(mascaraMoedaBR(e.target.value))} />
            </label>
          </div>

          <div className="tabela-wrap">
            <table>
              <thead><tr><th>Tipo</th><th>Descrição</th><th>Qtd</th><th>Valor unit.</th><th>Total</th><th /></tr></thead>
              <tbody>
                {rItens.map((it, i) => (
                  <tr key={i}>
                    <td>
                      <select value={it.tipo} onChange={(e) => setRItens((a) => a.map((x, j) => j === i ? { ...x, tipo: e.target.value } : x))}>
                        {TIPOS_ITEM.map((t) => <option key={t.v} value={t.v}>{t.t}</option>)}
                      </select>
                    </td>
                    <td style={{ minWidth: 260 }}>
                      {/* material e mão de obra puxam do cadastro; o texto continua editável depois */}
                      {it.tipo === 'material' && (
                        <select defaultValue="" onChange={(e) => aplicarMaterial(i, e.target.value)} style={{ marginBottom: 6 }}>
                          <option value="">
                            {materiais.length ? 'Puxar do estoque...' : 'Nenhum material cadastrado'}
                          </option>
                          {materiais.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.nome}{m.unidade_medida ? ` (${m.unidade_medida})` : ''} — R$ {fmtMoeda(m.valor_unitario)}
                            </option>
                          ))}
                        </select>
                      )}
                      {it.tipo === 'mao_obra' && (
                        <select defaultValue="" onChange={(e) => aplicarTecnico(i, e.target.value)} style={{ marginBottom: 6 }}>
                          <option value="">
                            {tecnicos.length ? 'Selecionar técnico...' : 'Nenhum técnico cadastrado'}
                          </option>
                          {tecnicos.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.nome}{num(t.valor_hora) > 0 ? ` — R$ ${fmtMoeda(t.valor_hora)}/h` : ' — sem valor/hora'}
                            </option>
                          ))}
                        </select>
                      )}
                      <input className="input" value={it.descricao} maxLength={180} placeholder="Descrição"
                             onChange={(e) => setRItens((a) => a.map((x, j) => j === i ? { ...x, descricao: e.target.value } : x))} />
                      {it.tipo === 'material' && (
                        <small style={{ display: 'block', marginTop: 4 }}>
                          <Link to="/app/estoque">cadastrar material</Link>
                        </small>
                      )}
                      {it.tipo === 'mao_obra' && (
                        <small style={{ display: 'block', marginTop: 4 }}>
                          <Link to="/app/usuarios">cadastrar técnico</Link>
                        </small>
                      )}
                    </td>
                    <td style={{ maxWidth: 90 }}>
                      <input className="input" inputMode="decimal" value={it.quantidade}
                             onChange={(e) => setRItens((a) => a.map((x, j) => j === i ? { ...x, quantidade: e.target.value } : x))} />
                    </td>
                    <td style={{ maxWidth: 120 }}>
                      <input className="input" inputMode="numeric" value={it.valor_unitario} placeholder="0,00"
                             onChange={(e) => setRItens((a) => a.map((x, j) => j === i ? { ...x, valor_unitario: mascaraMoedaBR(e.target.value) } : x))} />
                    </td>
                    <td>R$ {fmtMoeda(quantidadeParaNumero(it.quantidade) * moedaParaNumero(it.valor_unitario))}</td>
                    <td>
                      <button className="btn sec sm" disabled={rItens.length <= 1}
                              onClick={() => setRItens((a) => a.filter((_, j) => j !== i))}>remover</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <button className="btn sec sm" onClick={() => setRItens((a) => [...a, itemVazio()])}>+ item</button>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Total: R$ {fmtMoeda(totalRapido)}</div>
            <button className="btn" disabled={ocupado === -1} onClick={salvarRapido}>
              {ocupado === -1 ? 'Salvando...' : 'Criar cobrança'}
            </button>
          </div>
        </div>
      )}

      <div className="tabela-wrap">
        <table>
          <thead>
            <tr>
              <th>Cobrança</th>
              <th>Cliente</th>
              <th>Origem</th>
              <th>Total</th>
              <th>Parcelas</th>
              <th>Próx. vencimento</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lista.map((d) => {
              const st = CORES_STATUS[d.status] || { fundo: '#f1f5f9', cor: '#475569', rotulo: d.status };
              const aberta = abertoId === d.id;
              return (
                <tr key={d.id}>
                  <td colSpan={aberta ? 8 : 1} style={aberta ? { padding: 0 } : undefined}>
                    {!aberta ? (
                      <><strong>{d.codigo}</strong><br /><small style={{ color: 'var(--texto-suave)' }}>{fmtData(d.created_at)}</small></>
                    ) : (
                      <div style={{ padding: '14px 15px', background: '#fbfdff' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                          <div>
                            <strong style={{ fontSize: 16 }}>{d.codigo}</strong>{' '}
                            <span className="badge" style={{ background: st.fundo, color: st.cor }}>{st.rotulo}</span>
                            {d.aprovado_em && (
                              <>
                                {' '}
                                <span className="badge" style={{ background: '#f0fdf4', color: '#166534' }}
                                      title={`Aprovado pelo cliente em ${fmtData(d.aprovado_em)}${d.aprovado_nome ? ` por ${d.aprovado_nome}` : ''}`}>
                                  ✓ aprovado pelo cliente
                                </span>
                              </>
                            )}
                            <div style={{ marginTop: 4, color: 'var(--texto-suave)', fontSize: 13 }}>
                              {d.cliente_nome || 'sem cliente'} ·{' '}
                              {d.ordem_servico_id
                                ? <Link to={`/app/os/${d.ordem_servico_id}`}>OS {d.os_codigo}</Link>
                                : 'orçamento rápido'}
                              {d.descricao ? ` · ${d.descricao}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {d.status === 'rascunho' && (
                              <button className="btn sm" disabled={ocupado === d.id} onClick={() => mudarStatus(d, 'emitido')}>Emitir</button>
                            )}
                            {['rascunho', 'emitido'].includes(d.status) && (
                              <button className="btn sec sm" disabled={ocupado === d.id} onClick={() => mudarStatus(d, 'cancelado')}>Cancelar</button>
                            )}
                            {d.status === 'rascunho' && (
                              <button className="btn sec sm" disabled={ocupado === d.id} onClick={() => excluirRascunho(d)}>Excluir</button>
                            )}
                            {/* O link público recusa rascunho, então esses botões só a partir de emitido */}
                            {detalhe?.id === d.id && !['rascunho', 'cancelado'].includes(d.status) && (
                              <>
                                <a
                                  className="btn sm"
                                  href={linkWhatsapp(detalhe.cliente_whatsapp || detalhe.cliente_telefone, mensagemWhatsapp(detalhe))}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={detalhe.cliente_whatsapp || detalhe.cliente_telefone
                                    ? 'Enviar o link do recibo no WhatsApp do cliente'
                                    : 'Cliente sem WhatsApp cadastrado — abre o WhatsApp para você escolher o contato'}
                                >
                                  Enviar no WhatsApp
                                </a>
                                {detalhe.link_publico && (
                                  <a className="btn sec sm" href={detalhe.link_publico} target="_blank" rel="noreferrer">
                                    Ver recibo
                                  </a>
                                )}
                                {detalhe.link_pdf && (
                                  <a className="btn sec sm" href={detalhe.link_pdf} target="_blank" rel="noreferrer">
                                    PDF
                                  </a>
                                )}
                              </>
                            )}
                            {d.status === 'pago' && !d.ordem_servico_id && (
                              <button className="btn sm" onClick={() => abrirGerarOs(d)}>Gerar OS</button>
                            )}
                            <button className="btn sec sm" onClick={() => abrirDetalhe(d.id)}>Recolher</button>
                          </div>
                        </div>

                        {gerarOsDoc?.id === d.id && (
                          <div style={{ marginTop: 12, padding: 14, borderRadius: 14, border: '1px solid #dbe6f5', background: '#fff', display: 'grid', gap: 12 }}>
                            <strong style={{ color: '#0f172a' }}>Gerar OS do orçamento {d.codigo}</strong>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                              <label>
                                Tipo de chamado *
                                <select value={gTipoOs} onChange={(e) => setGTipoOs(e.target.value)}
                                        disabled={!tiposChamado.length}>
                                  <option value="">Selecione o tipo de chamado</option>
                                  {tiposChamado.map((c) => <option key={c.id} value={c.nome}>{c.nome}</option>)}
                                </select>
                                {!tiposChamado.length && (
                                  <small style={{ display: 'block', marginTop: 4, color: '#b45309' }}>
                                    Nenhum tipo de chamado cadastrado. Cadastre em{' '}
                                    <Link to="/app/cadastros">Cadastros &gt; Categorias</Link>.
                                  </small>
                                )}
                              </label>
                              <label>
                                Prioridade
                                <select value={gPrioridade} onChange={(e) => setGPrioridade(e.target.value)}>
                                  <option value="baixa">Baixa</option>
                                  <option value="media">Média</option>
                                  <option value="alta">Alta</option>
                                  <option value="urgente">Urgente</option>
                                </select>
                              </label>
                            </div>
                            <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
                              A OS nasce como <strong>aberta</strong>, com o cliente do orçamento e a descrição montada
                              a partir dos itens. Unidade, local e técnico você define depois, na própria OS.
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button className="btn" disabled={ocupado === -2} onClick={gerarOs}>
                                {ocupado === -2 ? 'Gerando...' : 'Gerar OS'}
                              </button>
                              <button className="btn sec" disabled={ocupado === -2} onClick={() => setGerarOsDoc(null)}>Cancelar</button>
                            </div>
                          </div>
                        )}

                        {!detalhe && <div style={{ marginTop: 12, color: 'var(--texto-suave)' }}>Carregando detalhe...</div>}

                        {detalhe?.id === d.id && (
                          <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--texto-suave)', marginBottom: 6 }}>
                                Como o total foi formado
                              </div>
                              <div className="tabela-wrap">
                                <table>
                                  <thead><tr><th>Tipo</th><th>Descrição</th><th>Qtd</th><th>Unit.</th><th>Total</th><th>Origem</th></tr></thead>
                                  <tbody>
                                    {(detalhe.itens || []).map((it) => (
                                      <tr key={it.id}>
                                        <td>{TIPOS_ITEM.find((t) => t.v === it.tipo)?.t || it.tipo}</td>
                                        <td>{it.descricao}</td>
                                        <td>{num(it.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</td>
                                        <td>R$ {fmtMoeda(it.valor_unitario)}</td>
                                        <td><strong>R$ {fmtMoeda(it.valor_total)}</strong></td>
                                        <td style={{ color: 'var(--texto-suave)' }}>
                                          {it.origem_tabela === 'materiais_movimentacoes' ? 'estoque da OS'
                                            : it.origem_tabela === 'ordens_servico' ? 'tempo da OS' : 'manual'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14 }}>
                                <span>Subtotal: <strong>R$ {fmtMoeda(detalhe.subtotal)}</strong></span>
                                <span>Desconto: <strong>R$ {fmtMoeda(detalhe.desconto)}</strong></span>
                                <span>Acréscimo: <strong>R$ {fmtMoeda(detalhe.acrescimo)}</strong></span>
                                <span style={{ fontSize: 16 }}>Total: <strong>R$ {fmtMoeda(detalhe.total)}</strong></span>
                              </div>
                            </div>

                            <div>
                              <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--texto-suave)', marginBottom: 6 }}>
                                Parcelas
                              </div>
                              <div className="tabela-wrap">
                                <table>
                                  <thead><tr><th>#</th><th>Valor</th><th>Vencimento</th><th>Situação</th><th>Baixa manual</th></tr></thead>
                                  <tbody>
                                    {(detalhe.parcelas || []).map((p) => (
                                      <tr key={p.id}>
                                        <td>{p.numero}</td>
                                        <td>R$ {fmtMoeda(p.valor)}</td>
                                        <td>{fmtData(p.vencimento)}</td>
                                        <td>
                                          {p.status === 'paga' ? (
                                            <span className="badge" style={{ background: '#f0fdf4', color: '#166534' }}>paga</span>
                                          ) : p.status === 'cancelada' ? (
                                            <span className="badge" style={{ background: '#fef2f2', color: '#b91c1c' }}>cancelada</span>
                                          ) : p.vencida ? (
                                            <span className="badge" style={{ background: '#fef2f2', color: '#b91c1c' }}>vencida</span>
                                          ) : (
                                            <span className="badge">aberta</span>
                                          )}
                                        </td>
                                        <td>
                                          {p.status === 'aberta' && detalhe.status === 'emitido' ? (
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                              {MEIOS.map((m) => (
                                                <button key={m.v} className="btn sec sm" disabled={ocupado === p.id}
                                                        onClick={() => darBaixa(p, m.v)} title={`Receber via ${m.t}`}>
                                                  {m.t}
                                                </button>
                                              ))}
                                            </div>
                                          ) : p.status === 'aberta' ? (
                                            <small style={{ color: 'var(--texto-suave)' }}>emita a cobrança primeiro</small>
                                          ) : (
                                            <small style={{ color: 'var(--texto-suave)' }}>{fmtData(p.pago_em)}</small>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  {!aberta && (
                    <>
                      <td>{d.cliente_nome || '-'}</td>
                      <td>
                        {d.ordem_servico_id
                          ? <Link to={`/app/os/${d.ordem_servico_id}`}>OS {d.os_codigo}</Link>
                          : <span style={{ color: 'var(--texto-suave)' }}>rápido</span>}
                      </td>
                      <td><strong>R$ {fmtMoeda(d.total)}</strong></td>
                      <td>
                        {d.parcelas_pagas}/{d.parcelas_qtd}
                        {d.parcelas_vencidas > 0 && (
                          <> <span className="badge" style={{ background: '#fef2f2', color: '#b91c1c' }}>{d.parcelas_vencidas} vencida(s)</span></>
                        )}
                      </td>
                      <td>{fmtData(d.proximo_vencimento)}</td>
                      <td>
                        <span className="badge" style={{ background: st.fundo, color: st.cor }}>{st.rotulo}</span>
                        {d.aprovado_em && (
                          <><br /><small style={{ color: '#166534', fontWeight: 700 }}>✓ aprovado</small></>
                        )}
                      </td>
                      <td><button className="btn sec sm" onClick={() => abrirDetalhe(d.id)}>Abrir</button></td>
                    </>
                  )}
                </tr>
              );
            })}
            {!lista.length && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>
                  {carregando ? 'Carregando...' : 'Nenhuma cobrança ainda. Crie a partir de uma OS na aba Prévia, ou use o orçamento rápido.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
